import * as React from 'react'
import { MiniTopic } from '@deepguide-ai/types'
import { FileText, Search, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTopicAtomsContext } from '@/hooks/useTopicAtomsContext'
import { DocIcon } from '@/components/ui/doc-icon'
import './page-search.scss'
import { useAtom } from 'jotai'
import { workspaceSlugAtom } from '@/stores/workspaceStore'
import { getTopicUrl } from '@/utils/navigation'
interface PageSearchProps {
  onSelect: (url: string, title?: string) => void
  onBack: () => void
  initialQuery?: string
  className?: string
}

export function PageSearch({
  onSelect,
  onBack,
  initialQuery = '',
  className,
}: PageSearchProps) {
  const [query, setQuery] = React.useState(initialQuery)
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const { allTopicsFlat } = useTopicAtomsContext()
  const [workspaceSlug] = useAtom(workspaceSlugAtom)

  // Filter topics based on search query
  const filteredTopics = React.useMemo(() => {
    if (!query) {
      return allTopicsFlat.slice(0, 10)
    }

    const searchQuery = query.toLowerCase()
    return allTopicsFlat
      .filter(
        (topic) =>
          topic.title.toLowerCase().includes(searchQuery) ||
          topic.description?.toLowerCase().includes(searchQuery)
      )
      .slice(0, 10)
  }, [query, allTopicsFlat])

  // Show URL option if query looks like a URL
  const showUrlOption = React.useMemo(() => {
    return (
      query.length > 0 &&
      (query.startsWith('http://') ||
        query.startsWith('https://') ||
        query.includes('.'))
    )
  }, [query])

  React.useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
      // Select all text to make it easy to replace
      searchInputRef.current.select()
    }
  }, [])

  React.useEffect(() => {
    setSelectedIndex(0)
  }, [filteredTopics])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const totalItems = filteredTopics.length + (showUrlOption ? 1 : 0)

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1))
        break
      case 'Enter':
        e.preventDefault()
        if (showUrlOption && selectedIndex === 0) {
          // Select URL option
          const url = query.startsWith('http') ? query : `https://${query}`
          onSelect(url)
        } else {
          const topicIndex = showUrlOption ? selectedIndex - 1 : selectedIndex
          const topic = filteredTopics[topicIndex]
          if (topic) {
            onSelect(buildInternalUrl(topic), topic.title)
          }
        }
        break
      case 'Escape':
        e.preventDefault()
        onBack()
        break
    }
  }

  const buildInternalUrl = (topic: MiniTopic) => {
    return getTopicUrl(workspaceSlug, topic)
  }

  const selectTopic = (topic: MiniTopic) => {
    onSelect(buildInternalUrl(topic), topic.title)
  }

  const selectUrl = () => {
    const url = query.startsWith('http') ? query : `https://${query}`
    onSelect(url)
  }

  return (
    <div className={cn('page-search', className)}>
      <div className='page-search-header'>
        <Search className='page-search-icon' />
        <input
          ref={searchInputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={(e) => e.target.select()}
          placeholder='Search for a page or paste a link...'
          className='page-search-input'
          type='text'
          autoComplete='off'
          autoCorrect='off'
          autoCapitalize='off'
        />
      </div>

      <div className='page-search-results'>
        {showUrlOption && (
          <button
            className={cn(
              'page-search-result-item',
              selectedIndex === 0 && 'page-search-result-item--selected'
            )}
            onClick={selectUrl}
            onMouseEnter={() => setSelectedIndex(0)}
          >
            <Globe className='page-search-result-icon' />
            <div className='page-search-result-content'>
              <div className='page-search-result-title'>Link to web page</div>
              <div className='page-search-result-url'>{query}</div>
            </div>
          </button>
        )}

        {filteredTopics.length > 0 && (
          <>
            {showUrlOption && <div className='page-search-divider' />}
            <div className='page-search-section-label'>Pages</div>
            {filteredTopics.map((topic, index) => {
              const itemIndex = showUrlOption ? index + 1 : index
              return (
                <button
                  key={topic.id}
                  className={cn(
                    'page-search-result-item',
                    itemIndex === selectedIndex &&
                      'page-search-result-item--selected'
                  )}
                  onClick={() => selectTopic(topic)}
                  onMouseEnter={() => setSelectedIndex(itemIndex)}
                >
                  {/* <FileText className="page-search-result-icon" /> */}
                  <DocIcon
                    topic={topic}
                    className={cn(topic.icon ? '' : 'mt-1')}
                  />
                  <div className='page-search-result-content'>
                    <div className='page-search-result-title'>
                      {topic.title}
                    </div>
                    {topic.description && (
                      <div className='page-search-result-description'>
                        {topic.description}
                      </div>
                    )}
                  </div>
                </button>
              )
            })}
          </>
        )}

        {filteredTopics.length === 0 && !showUrlOption && (
          <div className='page-search-empty'>
            <div className='page-search-empty-text'>No pages found</div>
            <div className='page-search-empty-hint'>
              Type a URL to link to an external page
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
